import { useRef, type FormEvent } from "react";
import type { FormField, FormId } from "../models/forms";
import { useFormPresenter } from "../presenters/useFormPresenter";
import { TimePicker } from "./TimePicker";
import { TurnstileWidget, type TurnstileHandle } from "./TurnstileWidget";

const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim() ?? "";

function formatWholeAmount(value: string | string[]): string {
  return String(value).replace(/\D/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function FieldLabel({ field }: { field: FormField }) {
  if (!field.labelLink) return field.label;

  const [beforeLink, afterLink] = field.label.split(field.labelLink.text);
  if (afterLink === undefined) return field.label;

  return (
    <>
      {beforeLink}
      <a
        className="field-label-link"
        href={field.labelLink.href}
        target="_blank"
        rel="noreferrer"
      >
        {field.labelLink.text}
      </a>
      {afterLink}
    </>
  );
}

function FieldView({
  field,
  value,
  error,
  onChange,
}: {
  field: FormField;
  value: string | string[];
  error?: string;
  onChange: (value: string | string[]) => void;
}) {
  const describedBy = `${field.id}-description ${field.id}-error`;
  const isCurrency = field.id === "remainingAmount";
  if (field.type === "section")
    return <h2 className="form-section-title">{field.label}</h2>;
  return (
    <div className={`form-field ${error ? "has-error" : ""}`}>
      <label htmlFor={field.type === "checkbox" ? undefined : field.id}>
        <FieldLabel field={field} />{" "}
        {field.required && <span className="required">(required)</span>}
      </label>
      {field.description && (
        <p id={`${field.id}-description`} className="field-description">
          {field.description}
        </p>
      )}
      {field.type === "textarea" && (
        <textarea
          id={field.id}
          value={String(value)}
          required={field.required}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
          rows={5}
        />
      )}
      {field.type === "time" && (
        <TimePicker
          id={field.id}
          value={String(value)}
          stepSeconds={field.step}
          describedBy={describedBy}
          invalid={Boolean(error)}
          onChange={onChange}
        />
      )}
      {isCurrency && (
        <div className="currency-input">
          <span className="currency-input-prefix" aria-hidden="true">NT$&#x20;</span>
          <input
            id={field.id}
            type="text"
            value={formatWholeAmount(value)}
            placeholder={field.placeholder}
            inputMode="numeric"
            aria-describedby={describedBy}
            onChange={(event) => onChange(event.target.value.split(".")[0].replace(/\D/g, ""))}
          />
        </div>
      )}
      {!isCurrency && ["text", "email", "tel", "date"].includes(field.type) && (
        <input
          id={field.id}
          type={field.type}
          value={String(value)}
          required={field.required}
          placeholder={field.placeholder}
          autoComplete={
            field.type === "email"
              ? "email"
              : field.type === "tel"
                ? "tel"
                : undefined
          }
          inputMode={field.type === "tel" ? "tel" : undefined}
          min={field.min}
          max={field.max}
          step={field.step}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
      {field.type === "select" && (
        <select
          id={field.id}
          value={String(value)}
          required={field.required}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">Select an option</option>
          {field.options?.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      )}
      {field.type === "checkbox" && (
        <fieldset aria-describedby={describedBy}>
          <legend className="sr-only">{field.label}</legend>
          {field.options?.map((option) => {
            const selected = Array.isArray(value) ? value : [];
            return (
              <label className="choice" key={option}>
                <input
                  type="checkbox"
                  checked={selected.includes(option)}
                  onChange={(event) =>
                    onChange(
                      event.target.checked
                        ? [...selected, option]
                        : selected.filter((item) => item !== option),
                    )
                  }
                />
                <span>{option}</span>
              </label>
            );
          })}
        </fieldset>
      )}
      {error && (
        <p id={`${field.id}-error`} className="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function FormView({ formId }: { formId: FormId }) {
  const presenter = useFormPresenter(formId);
  const turnstileRef = useRef<TurnstileHandle>(null);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void presenter.submit(turnstileRef.current?.getToken);
  };
  return (
    <section className={`form-page form-page-${formId}`}>
      <div className="form-shell">
        <h1>{presenter.definition.title}</h1>
        {presenter.definition.intro && (
          <p className="form-intro">{presenter.definition.intro}</p>
        )}
        {presenter.status === "success" ? (
          <div className="form-success" role="status">
            <p>{presenter.message}</p>
            {presenter.definition.successLinks && (
              <div className="form-success-links">
                {presenter.definition.successLinks.map((link) => {
                  const external = link.href.startsWith("http");
                  return (
                    <a key={link.href} href={link.href} {...(external ? { target: "_blank", rel: "noreferrer" } : {})}>
                      <strong>{link.label}</strong>
                      <span>{link.description}</span>
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={submit} noValidate>
            {formId === "remittance" && presenter.bookingContext && (
              <aside className="booking-context" aria-label="已辨識的訂單資訊">
                <p className="booking-context-eyebrow">已由安全連結辨識訂單</p>
                <strong>{presenter.bookingContext.guestName || "旅客"}</strong>
                <dl>
                  {presenter.bookingContext.orderReference && <><dt>訂單</dt><dd>{presenter.bookingContext.orderReference}</dd></>}
                  {presenter.bookingContext.checkInDate && <><dt>入住</dt><dd>{presenter.bookingContext.checkInDate}</dd></>}
                  {presenter.bookingContext.checkOutDate && <><dt>退房</dt><dd>{presenter.bookingContext.checkOutDate}</dd></>}
                  {presenter.bookingContext.nights > 0 && <><dt>晚數</dt><dd>{presenter.bookingContext.nights} 晚</dd></>}
                  {presenter.bookingContext.roomTypes.length > 0 && <><dt>房型</dt><dd>{presenter.bookingContext.roomTypes.join("、")}</dd></>}
                </dl>
              </aside>
            )}
            <div className="honeypot" aria-hidden="true">
              <label htmlFor={`${formId}-website`}>Company website</label>
              <input
                id={`${formId}-website`}
                tabIndex={-1}
                autoComplete="off"
                value={presenter.companyWebsite}
                onChange={(event) =>
                  presenter.setCompanyWebsite(event.target.value)
                }
              />
            </div>
            {presenter.definition.fields.map((field) => (
              <FieldView
                key={field.id}
                field={field}
                value={presenter.data[field.id] ?? ""}
                error={presenter.errors[field.id]}
                onChange={(value) => presenter.setValue(field.id, value)}
              />
            ))}
            {presenter.message && (
              <p className="form-message" role="alert">
                {presenter.message}
              </p>
            )}
            <p className="form-privacy-notice">
              {presenter.definition.privacyNotice}
              <a href="/privacy" target="_blank" rel="noreferrer">
                個人資料蒐集告知
              </a>
              。
            </p>
            {turnstileSiteKey && (
              <TurnstileWidget ref={turnstileRef} action={formId} siteKey={turnstileSiteKey} />
            )}
            <button
              className="submit-button"
              type="submit"
              disabled={presenter.status === "submitting"}
            >
              {presenter.status === "submitting" ? "SENDING…" : "SEND"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
